import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import { Edit } from 'lucide-react';

interface BlogCardProps {
  id: string;
  title: string;
  excerpt: string;
  slug: string;
  publishDate: string;
  categories: string[];
  featuredImage?: string;
}

const BlogCard: React.FC<BlogCardProps> = ({
  id,
  title,
  excerpt,
  slug,
  publishDate,
  categories,
  featuredImage,
}) => {
  // Get auth context to check if user is admin
  const { user } = useAuth();
  
  // Format date
  const formattedDate = formatDate(publishDate);

  return (
    <article className="group h-full relative">
      {/* Admin Edit Button - Only visible to admin users */}
      {user?.isAdmin && (
        <Link 
          to={`/admin?edit=${id}`} 
          className="absolute top-2 right-2 z-10 bg-white p-2 rounded-full shadow-md hover:bg-snudefryd-light-purple transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <Edit size={16} className="text-snudefryd-blue" />
        </Link>
      )}
      
      <Link to={`/blog/${slug}`} className="block h-full">
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden h-full flex flex-col">
          {/* Featured Image */}
          {featuredImage ? (
            <div className="aspect-video w-full overflow-hidden">
              <img 
                src={featuredImage}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="aspect-video w-full bg-gradient-to-r from-snudefryd-beige to-snudefryd-light-purple"></div>
          )}
          
          {/* Content */}
          <div className="p-4 md:p-5 flex-grow flex flex-col">
            {/* Categories */}
            {categories && categories.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {categories.slice(0, 2).map((category, index) => (
                  <span 
                    key={index} 
                    className="inline-block bg-snudefryd-light-purple text-snudefryd-blue text-xs px-2 py-1 rounded-full"
                  >
                    {category}
                  </span>
                ))}
                {categories.length > 2 && (
                  <span className="inline-block text-xs text-gray-500 px-2 py-1">
                    +{categories.length - 2} mere
                  </span>
                )}
              </div>
            )}
            
            {/* Title */}
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-snudefryd-blue transition-colors">
              {title}
            </h3>
            
            {/* Excerpt */}
            <p className="text-sm md:text-base text-gray-600 mb-3 line-clamp-2 flex-grow">
              {excerpt}
            </p>
            
            {/* Date */}
            <div className="mt-auto pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-500">{formattedDate}</p>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogCard; 